import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
  DOCUMENT,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ColumnFilter, Table, TableLazyLoadEvent, TableModule } from "primeng/table";
import { UiCoreLinkStopPropagationDirective } from "../../../../directives";
import { UiCoreEnumDescriptionPipe, UiCoreSanitizeHtmlPipe } from "../../../../pipes";
import { PagedResult, TableColumns } from "../../models";
import { ProgressBar } from "primeng/progressbar";
import { Button } from "primeng/button";
@Component({
  selector: "rr-ui-core-data-table",
  imports: [
    CommonModule,
    TableModule,
    UiCoreLinkStopPropagationDirective,
    UiCoreEnumDescriptionPipe,
    ProgressBar,
    Button,
    UiCoreSanitizeHtmlPipe,
  ],
  templateUrl: "./ui-core-data-table.component.html",
  styleUrl: "./ui-core-data-table.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiCoreDataTableComponent<T> {
  // DI
  readonly #document = inject(DOCUMENT);

  // I/O
  data = input.required<PagedResult<T>>();
  dataKey = input.required<string>();
  columns = input.required<TableColumns<T>[]>();
  resetFilters = input<boolean>(true);
  globalFilterTpl = input<TemplateRef<unknown>>();
  globalFilterFields = input<string[]>();
  lazy = input<boolean>(true);
  styleClass = input<string>("");
  size = input<"small" | "large" | undefined>("large");
  loading = input<boolean>(false);
  paginator = input<boolean>(true);
  showCurrentPageReport = input<boolean>(false);
  canSelectAll = input<boolean>(true);
  selectable = input<boolean>(true);
  mode = input<"single" | "multiple">("multiple");
  selectedItems = input<T[]>();
  noDataMessage = input<string>("No results found.");
  getTableData = output<TableLazyLoadEvent>();
  // Reason: This can be a single or multiple selection, following PrimeNG's behavior.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectItems = output<any>();

  //Template variables
  dt = viewChild<Table>("dt");
  readonly #tempFilters = signal<unknown>(null);
  selectionMode = computed<"single" | "multiple" | null>(() => (this.selectable() ? this.mode() : null));

  constructor() {
    effect(() => {
      if (this.resetFilters()) {
        this.resetTempFilters();
        this.dt()?.reset();
      }
    });
  }

  private resetTempFilters() {
    this.#tempFilters.set(null);
  }

  assignFilter = (value: unknown) => {
    this.#tempFilters.set(value);
  };

  onClearFilter(filterCallback: (value: unknown) => void, colFilter: ColumnFilter) {
    this.resetTempFilters();
    this.onApplyFilter(filterCallback, colFilter);
  }

  onApplyFilter(filterCallback: (value: unknown) => void, colFilter: ColumnFilter) {
    filterCallback(this.#tempFilters());
    colFilter.hide();
  }

  onShowFilter() {
    this.#document.querySelector(".p-datatable-filter-overlay-popover")?.classList.add("h-0", "p-0", "invisible");
  }
}
